import { computed, inject, Injectable, Signal } from '@angular/core';

import { ManagementPanelEnum } from '@connect-services/client/components/management/management-panel/management-panel.component';
import { HasPermissionsService, OperatorEnum } from '@connect-services/client/services/has-permissions.service';
import { PermissionAliasEnum, Role } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class OrganizationAccessPermissionsService {
    public readonly initialized: Signal<boolean> = computed(() => this.hasPermissionsService.initialized());

    private readonly hasPermissionsService = inject(HasPermissionsService);

    public canAccessOrganization(): boolean {
        return this.canAccessUser() || this.canAccessRole();
    }

    public canAccessRole(): boolean {
        return this.hasPermissionsService.checkPermissions(
            [PermissionAliasEnum.MANAGE_ROLE, PermissionAliasEnum.READ_ROLE],
            OperatorEnum.OR,
        );
    }

    public canAccessUser(): boolean {
        return this.hasPermissionsService.checkPermissions(
            [PermissionAliasEnum.MANAGE_PERSON, PermissionAliasEnum.READ_PERSON],
            OperatorEnum.OR,
        );
    }

    public canAccessUserInterventions(): boolean {
        return this.hasPermissionsService.checkPermissions(
            [PermissionAliasEnum.MANAGE_FIELD_INTERVENTION, PermissionAliasEnum.READ_FIELD_INTERVENTION],
            OperatorEnum.OR,
        );
    }

    public canAccessUserProjects(): boolean {
        return this.hasPermissionsService.checkPermissions(
            [PermissionAliasEnum.MANAGE_PROJECT, PermissionAliasEnum.READ_PROJECT],
            OperatorEnum.OR,
        );
    }

    public canEditUser(): boolean {
        return this.hasPermissionsService.checkPermissions(
            [PermissionAliasEnum.MANAGE_PERSON, PermissionAliasEnum.MANAGE_PROJECT],
            OperatorEnum.OR,
        );
    }

    public canManageRole(): boolean {
        return this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_ROLE]);
    }

    public canManageUser(): boolean {
        return this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_PERSON]);
    }

    public getDeleteRoleState(role?: Role | null): {
        allowed: boolean;
        disabledReasonKey?: string;
    } {
        if (!this.canManageRole() || !role) {
            return { allowed: false, disabledReasonKey: 'insufficientPermissions' };
        }

        if ((role.assignmentCount ?? 0) > 0) {
            return { allowed: false, disabledReasonKey: 'noUserMustBeAssociatedToThisRole' };
        }

        return { allowed: true };
    }

    public usersRolesLabelKey(): ManagementPanelEnum {
        const canAccessUser = this.canAccessUser();
        const canAccessRole = this.canAccessRole();

        if (canAccessUser && canAccessRole) {
            return ManagementPanelEnum.USERS_AND_ROLES;
        }

        if (canAccessUser) {
            return ManagementPanelEnum.USERS;
        }

        if (canAccessRole) {
            return ManagementPanelEnum.ROLES;
        }

        return ManagementPanelEnum.NO_PERMISSIONS;
    }
}
